import {
    AfterContentInit, DestroyRef, Directive, inject, input
} from "@angular/core";
import { coerceBooleanProperty } from "@angular/cdk/coercion";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NgControl } from "@angular/forms";
import { MatCheckbox } from "@angular/material/checkbox";
import { startWith } from "rxjs";

import { BulkUpdateFormUtil } from "../../utils/bulk-update-form-util";

/**
 * Makes a `<mat-checkbox>` a tri-state field of a Bulk Update Form.
 *
 * The control holds `true`, `false`, or `null`. `null` is shown indeterminate and means *leave
 * alone*: it is the seed of a selection whose records disagree, and a click moves it on to `true`.
 * When `mixed` is `true` the cycle continues `true` → `false` → `null`, so the user can return a
 * mixed field to *leave* without cancelling; when the records share a value the checkbox toggles
 * between `true` and `false` and returning to the seed is a leave. The cycle is
 * {@link BulkUpdateFormUtil.nextCheckboxValue}, which hotkey handlers use too.
 *
 * Must be on a `mat-checkbox` bound to a reactive control (`formControlName` or `[formControl]`); it does
 * not matter whether `ReactiveFormsModule` or this directive comes first in the host's `imports`. A checkbox
 * with no control throws a descriptive error.
 * See docs/angular-standards/components/04-forms.md#bulk-update-form.
 *
 * @example
 * ```html
 * <mat-checkbox mgdCheckbox formControlName="topDeal" [mgdBulkCheckbox]="isMixed('topDeal')">Top Deal</mat-checkbox>
 * @if (isMixed("topDeal") && formGroup.controls.topDeal.value === null) {
 *   <span>Mixed values</span>
 * }
 * ```
 *
 * @selector mat-checkbox[mgdBulkCheckbox]
 */
@Directive({ selector: "mat-checkbox[mgdBulkCheckbox]" })
export class BulkCheckboxDirective implements AfterContentInit {

    /** Whether the selected records disagree on this field, which adds the return-to-leave step to the cycle. */
    readonly mgdBulkCheckbox = input(false, { transform: coerceBooleanProperty });

    private readonly _checkbox = inject(MatCheckbox, { self: true });

    private readonly _ngControl = inject(NgControl, { self: true });

    private readonly _destroyRef = inject(DestroyRef);

    /** The control's value before the latest change, i.e. before the click being handled. */
    private _previous: boolean | null | undefined = null;

    /** The control's latest value. */
    private _current: boolean | null | undefined = null;

    /**
     * Wires the control once the host's own `ngOnChanges`/`ngOnInit` hooks have all run. `FormControlName` only
     * resolves its `control` in `ngOnChanges`, and directives on one element run those hooks in the order they
     * match, so reading the control in `ngOnInit` would depend on `ReactiveFormsModule` preceding this directive in
     * the host's `imports`. `ngAfterContentInit` runs after every directive on the element has been initialised.
     */
    ngAfterContentInit(): void {

        const control = this._ngControl.control;
        if (!control) {
            throw new Error("mgdBulkCheckbox requires a reactive form control (formControlName, formControl or ngModel) on the same mat-checkbox");
        }

        // Material's click action only knows true/false, so the indeterminate flag follows the control's null
        control.valueChanges.pipe(
            startWith(control.value as boolean | null),
            takeUntilDestroyed(this._destroyRef)
        )
            .subscribe((value) => {
                this._previous = this._current;
                this._current = value;
                this._checkbox.indeterminate = value === null || value === undefined;
            });

        // Material has already written its own toggle to the control when `change` fires; the cycle replaces it
        this._checkbox.change.pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(() => {
                const next = BulkUpdateFormUtil.nextCheckboxValue(this._previous, this.mgdBulkCheckbox());
                if (next !== this._current) {
                    control.setValue(next);
                }
            });
    }
}
